// Every action that takes time, given the same four parts. Factory-owned.
//
// WHY THIS EXISTS. It is the chrome problem again, one layer in. The factory
// specifies what a screen contains and says nothing about what a control DOES
// between the tap and the result — so every agent invents it, and most of them
// invent nothing: a button that fires an async handler, looks identical for
// 900ms, and gets tapped again. "No feedback on action" and "ignoring edge
// cases" are the two most common microinteraction defects, and they are both
// structural rather than careless.
//
// A microinteraction has four parts (Saffer): a TRIGGER starts it, RULES decide
// what happens, FEEDBACK says what is happening, and LOOPS decide how it
// behaves the hundredth time. This module owns the middle two so a page only
// has to write the trigger and the work.
//
// WHAT IT GUARANTEES, and each one is a diagnostic row the chassis used to fail:
//
//   · feedback inside 100ms          `busy` flips synchronously, before the
//                                    first await — not after the network
//                                    answers, which is the whole point
//   · one action, not three          re-entry while busy is dropped, so a
//                                    double-tap cannot double-charge
//   · feedback scaled to the event   a tick on a small action, a pop on a
//                                    completed one; nothing celebratory
//   · errors land in place           the message is returned to the caller to
//                                    render beside the control, not thrown
//                                    into a toast the user has to chase
//   · honest progress                there is no fake percentage here. If the
//                                    work cannot report progress, the state is
//                                    `busy` and the UI shows a spinner
//
// USE:
//
//   const save = useAction(async () => { await api.save(form); }, {
//     done: 'Saved',
//   });
//
//   <IonButton :disabled="save.busy.value" @click="save.run()">
//     {{ save.busy.value ? 'Saving…' : 'Save' }}
//   </IonButton>
//   <p v-if="save.error.value" class="field-error">{{ save.error.value }}</p>

import { ref, readonly } from 'vue';
import { haptic, toast } from '../util.js';
import { announce } from '../a11y.js';

/**
 * @param {() => Promise<any>} work    what the action actually does
 * @param {object} [options]
 * @param {string} [options.done]      a short confirmation, or '' for none.
 *                                     Only for work whose RESULT is not visible
 *                                     on screen — a toast confirming something
 *                                     the user can already see is noise.
 * @param {string} [options.failed]    what to say when it throws. The default
 *                                     names the action rather than the stack.
 * @param {'tick'|'pop'|'none'} [options.weight]
 *                                     how big the haptic is. Scale it to the
 *                                     event: `tick` for a toggle, `pop` for
 *                                     something completed or destroyed.
 */
export function useAction(work, options = {}) {
  const { done = '', failed = 'That did not work. Try again.', weight = 'tick' } = options;

  const busy = ref(false);
  const error = ref('');

  async function run(...args) {
    // THE EDGE CASE THAT COSTS MONEY: a repeated trigger. Dropped rather than
    // queued — a second tap on Pay means "did that register", not "pay twice".
    if (busy.value) return undefined;

    busy.value = true;
    error.value = '';
    // Before the first await, so the control changes in the same frame as the
    // tap. Feedback after the work has started is feedback the user has already
    // stopped waiting for.
    if (weight !== 'none') haptic(weight);

    try {
      const result = await work(...args);
      if (done) {
        toast(done);
        // A toast is invisible to a screen reader unless something says so.
        announce(done);
      }
      return result;
    } catch (e) {
      // In place, next to the control, and never only in the console. The
      // message is RETURNED as state rather than thrown, because a rejected
      // promise from a click handler is an unhandled rejection and a silent
      // failure at the same time.
      error.value = e?.message ? String(e.message) : failed;
      announce(error.value);
      return undefined;
    } finally {
      busy.value = false;
    }
  }

  return { run, busy: readonly(busy), error: readonly(error) };
}

/**
 * A long loop: show something only until it has been seen enough times.
 *
 * The first three sessions get the hint; the fiftieth does not. Progressive
 * reduction is the part of a microinteraction that nobody builds, because on
 * the day it is written every use is the first one — so the chassis counts, and
 * a page only asks.
 *
 * Storage is per key and survives a reload; a private window that cannot store
 * simply shows the hint every time, which is the safe direction to fail.
 *
 * @param {string} key    a stable name for the hint
 * @param {number} [times] how many times it is worth showing
 */
export function showUntilLearned(key, times = 3) {
  const name = `hint.${key}`;
  let seen = 0;
  try { seen = Number(localStorage.getItem(name) ?? 0) || 0; } catch { seen = 0; }
  if (seen >= times) return false;
  try { localStorage.setItem(name, String(seen + 1)); } catch { /* private mode */ }
  return true;
}
